import React, { FC } from 'react'
import { GetServerSideProps } from 'next'
import { CompetitionClientModel } from '@sport1/types/sportData'
import TeamContext from '@/context/TeamContext'
import StandardLayout from '@/layouts/Standard'
import PageContentLayout from '@/layouts/Standard/PageContentLayout'
import { TeamPageProps } from '@/types/page'
import handleRejection, { PageNotFound } from '@/utils/errors/handleRejection'
import getTeamPageSerSideProps from '@/helpers/teamHelper'
import LayoutValueContext from '@/context/LayoutValueContext'
import CompetitionDropDown, {
    TeamStatisticsDropdownKeys,
    TeamStatisticsDropdownValueType,
} from '@/components/CompetitionDropDown'
import { getTeamCompetitions } from '@/pages/api/cms/team/[id]'

type TeamStatisticsProps = TeamPageProps & {
    competitions: CompetitionClientModel[]
}

const Index: FC<TeamStatisticsProps> = ({ layoutData, navigation, team, competitions }) => {
    const options = React.useMemo(
        () =>
            competitions
                ?.filter(c => c.hasStatistics)
                ?.map(c => ({
                    label: c.name ?? '',
                    value: c.id ?? '',
                })) ?? [],
        [competitions]
    )

    return (
        <StandardLayout navigation={navigation}>
            {layoutData ? (
                <TeamContext team={team}>
                    <LayoutValueContext<TeamStatisticsDropdownKeys, TeamStatisticsDropdownValueType>
                        defaultSelected={{ competitions: options[0] }}
                        defaultValues={{
                            competitions: options,
                        }}
                    >
                        <CompetitionDropDown componentKey="statistics-layout" />
                        <PageContentLayout layoutData={layoutData} />
                    </LayoutValueContext>
                </TeamContext>
            ) : null}
        </StandardLayout>
    )
}

export const getServerSideProps: GetServerSideProps<
    TeamStatisticsProps | PageNotFound
> = async ctx => {
    const teamId = ctx.query.id as string
    const widget = 'statistiken'
    const props = await getTeamPageSerSideProps({
        context: ctx,
        teamId,
        widget,
    })

    if ('notFound' in props) {
        return props
    }

    let competitions: CompetitionClientModel[] = []
    try {
        if (props.navigation.tag?.params?.length) {
            const sportIdentifier = props.navigation.tag.params?.find(
                p => p.format === 'SPORT_IDENTIFIER'
            )
            if (sportIdentifier) {
                competitions = await getTeamCompetitions(sportIdentifier.value, teamId)
            }
        }
    } catch (error) {
        return handleRejection(error, { teamId, widget })
    }
    return { props: { ...props, competitions } }
}

export default Index
